var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** ⚠️ keep this order of import. @see https://docs.ethers.io/v5/cookbook/react-native/#cookbook-reactnative ⚠️ */
import { ethers } from "ethers";
import BigNumber from "bignumber.js";
import { log } from "@ledgerhq/logs";
import { delay } from "@ledgerhq/live-promise";
import { makeLRUCache } from "@ledgerhq/live-network/cache";
import OptimismGasPriceOracleAbi from "../../abis/optimismGasPriceOracle.abi.json";
import { GasEstimationError, InsufficientFunds } from "../../errors";
import { transactionToEthersTransaction } from "../../adapters";
import { getSerializedTransaction } from "../../transaction";
import { isExternalNodeConfig } from "./types";
import ERC20Abi from "../../abis/erc20.abi.json";
export const RPC_TIMEOUT = process.env.NODE_ENV === "test" ? 100 : 5000; // wait 5 sec after a fail
export const DEFAULT_RETRIES_RPC_METHODS = process.env.NODE_ENV === "test" ? 1 : 3;
/**
 * Cache for RPC providers to avoid recreating the connection on every usage of `withApi`
 * Without this, ethers will create a new provider and use the `eth_chainId` RPC call
 * at instanciation which could result in rate limits being reached
 * on some specific nodes (E.g. the main Optimism RPC)
 */
const PROVIDERS_BY_RPC = {};
/**
 * Connects to RPC Node
 *
 * ⚠️ Make sure to always use a `StaticJsonRpcProvider` and not a `JsonRpcProvider`
 * because the latter will use a `eth_chainId` before every request in order
 * to check if the node used changed (as per EIP-1193 standard)
 * @see https://github.com/ethers-io/ethers.js/issues/901
 */
export function withApi(currency, execute, retries = DEFAULT_RETRIES_RPC_METHODS) {
    return __awaiter(this, void 0, void 0, function* () {
        const { node } = currency.ethereumLikeInfo || {};
        if (!isExternalNodeConfig(node)) {
            throw new Error("Currency doesn't have an RPC node provided");
        }
        try {
            if (!PROVIDERS_BY_RPC[node.uri]) {
                PROVIDERS_BY_RPC[node.uri] = new ethers.providers.StaticJsonRpcProvider(node.uri);
            }
            const provider = PROVIDERS_BY_RPC[node.uri];
            return yield execute(provider);
        }
        catch (e) {
            if (retries) {
                // wait the RPC timeout before trying again
                yield delay(RPC_TIMEOUT);
                // decrement with prefix here or it won't work
                return withApi(currency, execute, --retries);
            }
            throw e;
        }
    });
}
/**
 * Get a transaction by hash
 */
export const getTransaction = (currency, txHash) => withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    const { hash, blockNumber: blockHeight, blockHash, nonce } = yield api.getTransaction(txHash);
    return {
        hash,
        blockHeight,
        blockHash,
        nonce,
    };
}));
/**
 * Get the balance of an address
 */
export const getCoinBalance = (currency, address) => withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    const balance = yield api.getBalance(address);
    return new BigNumber(balance.toString());
}));
/**
 * Get the balance of an address
 */
export const getTokenBalance = (currency, address, contractAddress) => withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    const erc20 = new ethers.Contract(contractAddress, ERC20Abi, api);
    const balance = yield erc20.balanceOf(address);
    return new BigNumber(balance.toString());
}));
/**
 * Get account nonce
 */
export const getTransactionCount = (currency, address) => withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    return api.getTransactionCount(address, "pending");
}));
/**
 * Get an estimated gas limit for a transaction
 */
export const getGasEstimation = (account, transaction) => withApi(account.currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    const { to, value, data } = transactionToEthersTransaction(transaction);
    try {
        const gasEtimation = yield api.estimateGas({
            from: account.freshAddress,
            to,
            value,
            data,
        });
        return new BigNumber(gasEtimation.toString());
    }
    catch (e) {
        log("error", "EVM Family: Gas Estimation Error", e);
        throw new GasEstimationError();
    }
}));
/**
 * Get an estimation of fees on the network
 */
export const getFeeData = currency => withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    const block = yield api.getBlock("latest");
    const currencySupports1559 = Boolean(block.baseFeePerGas);
    const feeData = yield (() => __awaiter(void 0, void 0, void 0, function* () {
        if (currencySupports1559) {
            const feeHistory = yield api.send("eth_feeHistory", [
                "0x5",
                "latest",
                [50], // 50% percentile sample
            ]);
            // Taking the average priority fee used on the last 5 blocks
            const maxPriorityFeeAverage = feeHistory.reward
                .reduce((acc, [curr]) => acc.plus(new BigNumber(curr)), new BigNumber(0))
                .dividedToIntegerBy(feeHistory.reward.length);
            // A maxPriorityFeePerGas too low might make a transaction stuck forever
            // As a safety measure, if maxPriorityFeePerGas is zero
            // we enforce a 1 Gwei value
            const maxPriorityFeePerGas = maxPriorityFeeAverage.isZero()
                ? new BigNumber(1e9) // 1 Gwei
                : maxPriorityFeeAverage;
            const nextBaseFee = new BigNumber(feeHistory.baseFeePerGas[feeHistory.baseFeePerGas.length - 1]);
            return {
                maxPriorityFeePerGas,
                maxFeePerGas: nextBaseFee.multipliedBy(2).plus(maxPriorityFeePerGas),
                nextBaseFee,
            };
        }
        else {
            const gasPrice = yield api.getGasPrice();
            return {
                gasPrice,
            };
        }
    }))();
    return {
        maxFeePerGas: feeData.maxFeePerGas ? new BigNumber(feeData.maxFeePerGas.toString()) : null,
        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
            ? new BigNumber(feeData.maxPriorityFeePerGas.toString())
            : null,
        gasPrice: feeData.gasPrice ? new BigNumber(feeData.gasPrice.toString()) : null,
        nextBaseFee: feeData.nextBaseFee ? new BigNumber(feeData.nextBaseFee.toString()) : null,
    };
}));
/**
 * Broadcast a serialized transaction and returns its hash
 */
export const broadcastTransaction = (currency, signedTxHex) => withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const { hash } = yield api.sendTransaction(signedTxHex);
        return hash;
    }
    catch (e) {
        if (e.code === "INSUFFICIENT_FUNDS") {
            log("error", "EVM Family: Wrong estimation of fees", e);
            throw new InsufficientFunds();
        }
        throw e;
    }
}), 0);
/**
 * Get the informations about a block by block height
 */
export const getBlockByHeight = (currency, blockHeight = "latest") => withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
    const { hash, number, timestamp } = yield api.getBlock(blockHeight);
    return {
        hash,
        height: number,
        timestamp,
    };
}));
/**
 * ⚠️ Blockchain specific
 *
 * For a layer 2 like Optimism, additional fees are needed in order to
 * take into account layer 1 settlement estimated cost.
 * This gas price is served through a smart contract oracle.
 *
 * @see https://help.optimism.io/hc/en-us/articles/4411895794715-How-do-transaction-fees-on-Optimism-work-
 */
export const getOptimismAdditionalFees = makeLRUCache((currency, transaction) => __awaiter(void 0, void 0, void 0, function* () {
    return withApi(currency, (api) => __awaiter(void 0, void 0, void 0, function* () {
        if (!["optimism", "optimism_goerli", "base", "base_goerli"].includes(currency.id)) {
            return new BigNumber(0);
        }
        // Fake signature is added to get the best approximation possible for the gas on L1
        const serializedTransaction = (() => {
            try {
                return getSerializedTransaction(transaction, {
                    r: "0xffffffffffffffffffffffffffffffffffffffff",
                    s: "0xffffffffffffffffffffffffffffffffffffffff",
                    v: 0,
                });
            }
            catch (e) {
                return null;
            }
        })();
        if (!serializedTransaction) {
            return new BigNumber(0);
        }
        const optimismGasOracle = new ethers.Contract(
        // contract address provided here
        // @see https://community.optimism.io/docs/developers/build/transaction-fees/#displaying-fees-to-users
        "0x420000000000000000000000000000000000000F", OptimismGasPriceOracleAbi, api);
        const additionalL1Fees = yield optimismGasOracle.getL1Fee(serializedTransaction);
        return new BigNumber(additionalL1Fees.toString());
    }));
}), (currency, transaction) => {
    const serializedTransaction = (() => {
        try {
            return getSerializedTransaction(transaction);
        }
        catch (e) {
            return null;
        }
    })();
    return "getOptimismL1BaseFee_" + currency.id + "_" + serializedTransaction;
}, { ttl: 15 * 1000 });
const node = {
    getBlockByHeight,
    getCoinBalance,
    getTokenBalance,
    getTransactionCount,
    getTransaction,
    getGasEstimation,
    getFeeData,
    broadcastTransaction,
    getOptimismAdditionalFees,
};
export default node;
//# sourceMappingURL=rpc.common.js.map